import { createContext, useCallback, useContext, useEffect, useMemo } from "react"

export const OverlayContext = createContext({
  dialogRef: undefined,
  loadingRef: undefined,
})
OverlayContext.displayName = "OverlayContext"

export const useLoadingIndicator = () => {
  const { loadingRef } = useContext(OverlayContext)
  return useCallback(
    (promise) => {
      const finishCallback = loadingRef.current.open()
      return promise.finally(finishCallback)
    },
    [loadingRef],
  )
}

export const useDialogs = () => {
  const source = useMemo(() => Symbol(), [])
  const { dialogRef } = useContext(OverlayContext)

  const openModal = useCallback(
    (type) => (message, options) =>
      dialogRef.current.open({
        type,
        message,
        options,
        source,
      }),
    [dialogRef, source],
  )

  const openMessageModal = useMemo((message, options) => openModal("message"), [openModal])

  const openInfoModal = useMemo((message, options) => openModal("info"), [openModal])

  const openSuccessModal = useMemo((message, options) => openModal("success"), [openModal])

  const openWarningModal = useMemo((message, options) => openModal("warning"), [openModal])

  const openConfirmModal = useMemo((message, options) => openModal("confirm"), [openModal])

  const openErrorModal = useMemo((message, options) => openModal("error"), [openModal])

  useEffect(
    () => () => {
      // close modals when unmount
      if (dialogRef && dialogRef.current) {
        // check to avoid error with hot reload
        dialogRef.current.clear(source)
      }
    },
    [dialogRef, source],
  )

  return {
    openMessageModal,
    openInfoModal,
    openSuccessModal,
    openWarningModal,
    openBlockModal: openInfoModal, // TODO remove it after replaced with openInfoModal
    openConfirmModal,
    openErrorModal,
  }
}
